import { Fragment, useLayoutEffect, useRef, useState } from 'react';

import { Transition } from '@headlessui/react';
import { ChevronDown, ExternalLink, FileSearch, Gauge, History, Megaphone, Sparkles, ThumbsDown, ThumbsUp, UserRound } from 'lucide-react';
import moment from 'moment';

import Chip from '../../crm.chip';
import ClampedText from '../../../shared/display/ClampedText';
import SectionLabel from '../../../shared/display/SectionLabel';

import datetimeProvider from '../../../../providers/datetime.provider';
import crmTriageBriefingTrackingService from '../../../../domain/services/crm/crm.triage.briefing.tracking.service';

import { pageTrackingExcludeProps } from '../../../../domain/constant/marketing/page.tracking.constant';

import {
    TRIAGE_BRIEFING_HEADLINE_LINES,
    triageBriefingFeedbackTexts,
    triageBriefingProfiles,
    triageBriefingProseKeys,
    triageBriefingReadingKey,
    triageBriefingAnalysisParts,
    triageBriefingHiddenFactKeys,
    triageBriefingReadingTexts,
    triageBriefingReferenceTexts,
    triageBriefingTexts,
    triageBriefingUses,
    triageBriefingUseTitles,
} from '../../../../domain/constant/crm/crm.triage.briefing.constant';

const REVEAL_CLASS = 'animate-briefing-rise motion-reduce:animate-none';
const DISCLOSURE_EASE = 'ease-[cubic-bezier(0.25,0.1,0.25,1)] motion-reduce:transition-none';
const WEB_PROTOCOLS = ['http:', 'https:'];
const WWW_PREFIX = 'www.';
const SECTION_STAGGER_MS = 50;
const VALUE_CLAMP_CLASS = 'line-clamp-3';
const ANALYSIS_PARAGRAPH_CLASS = 'mt-2 break-words text-[13px] leading-relaxed';
const ANALYSIS_TEXT_CLASS = 'text-gray-700';
const RECOMMENDATION_CLASS = 'rounded-[10px] bg-white px-3 py-2 font-medium text-gray-900 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04]';
const NOT_TRACKED = Symbol('notTracked');
const CLOSED = Symbol('closed');

const stylesByUse = {
    [triageBriefingUses.who]: { Icon: UserRound, tone: 'bg-primaryTint text-primary' },
    [triageBriefingUses.arrival]: { Icon: Megaphone, tone: 'bg-blue-50 text-blue-600' },
    [triageBriefingUses.history]: { Icon: History, tone: 'bg-violet-50 text-violet-600' },
    [triageBriefingUses.sizing]: { Icon: Gauge, tone: 'bg-green-50 text-green-700' },
};

const neutralUseStyle = { Icon: FileSearch, tone: 'bg-gray-100 text-gray-500' };

const isValidDate = (value) => moment(value, moment.ISO_8601, true).isValid();

const isShownFact = (fact) => !triageBriefingHiddenFactKeys.includes(fact.key);

const isProse = (fact) => triageBriefingProseKeys.includes(fact.key);

const isSameRow = (row, fact) => !isProse(row) && !isProse(fact) && row.label === fact.label;

const rowOf = (fact) => ({ key: fact.key, label: fact.label, entries: [fact] });

const withFactInRow = (rows, fact) => {

    const index = rows.findIndex(row => isSameRow(row, fact));

    return index < 0
        ? [...rows, rowOf(fact)]
        : rows.map((row, position) => (position === index ? { ...row, entries: [...row.entries, fact] } : row));
};

const filledSections = (briefing) => (briefing.sections || [])
    .map(section => ({ ...section, rows: (section.facts || []).filter(isShownFact).reduce(withFactInRow, []) }))
    .filter(section => section.rows.length);

const isWebUrl = (url) => typeof url === 'string' && WEB_PROTOCOLS.some(protocol => url.startsWith(`${protocol}//`));

const siteNameOf = (url) => {

    try {

        const { hostname } = new URL(url);

        return hostname.startsWith(WWW_PREFIX) ? hostname.slice(WWW_PREFIX.length) : hostname;
    } catch (error) {
        console.warn('crm:triage-briefing:referenceUrlInvalid', error.message);

        return null;
    }
};

const referencesOf = (briefing) => {

    const { used, analyzed } = briefing.references ?? {};

    return Number.isInteger(analyzed) && analyzed > 0 && Array.isArray(used)
        ? { used: used.filter(reference => isWebUrl(reference?.url) && siteNameOf(reference.url)), analyzed }
        : null;
};

const withWhoSection = (sections, who) => {

    if (!who || sections.some(section => section.use === triageBriefingUses.who))
        return sections;

    return [{ use: triageBriefingUses.who, rows: [] }, ...sections];
};

const trimmedText = (value) => (typeof value === 'string' ? value.trim() : '');

const paragraphsOf = (text) => text.split('\n').map(line => line.trim()).filter(Boolean);

const analysisOf = (reading) => {

    const analysis = reading?.[triageBriefingReadingKey];

    if (!analysis || typeof analysis !== 'object')
        return null;

    const parts = triageBriefingAnalysisParts.map(part => ({ ...part, paragraphs: paragraphsOf(trimmedText(analysis[part.key])) }));

    return parts.every(part => part.paragraphs.length) ? parts : null;
};

const whoOf = (briefing) => {

    const portrait = trimmedText(briefing.portrait);

    return portrait ? { text: portrait } : null;
};

const isOverflowing = (element) => element.scrollHeight > element.clientHeight;

const useClampedOverflow = (value, isExpanded) => {

    const valueRef = useRef(null);
    const [isClamped, setIsClamped] = useState(false);

    useLayoutEffect(() => {

        const element = valueRef.current;

        if (isExpanded || !element)
            return undefined;

        const measure = () => setIsClamped(isOverflowing(element));
        const observer = new ResizeObserver(measure);

        measure();
        observer.observe(element);

        return () => observer.disconnect();
    }, [value, isExpanded]);

    return [valueRef, isClamped];
};

const FactValue = ({ value, className }) => {

    const [isExpanded, setIsExpanded] = useState(false);
    const [valueRef, isClamped] = useClampedOverflow(value, isExpanded);

    return (
        <span className={`min-w-0 ${className}`}>
            <span
                ref={valueRef}
                title={typeof value === 'string' ? value : undefined}
                className={`break-words text-[13px] text-gray-900 ${isExpanded ? 'block' : VALUE_CLAMP_CLASS}`}
            >
                {value}
            </span>
            {isClamped && (
                <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="mt-0.5 text-[12px] font-medium text-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                >
                    {isExpanded ? triageBriefingTexts.showLess : triageBriefingTexts.showMore}
                </button>
            )}
        </span>
    );
};

const SourceLink = ({ fact }) => {

    if (!isWebUrl(fact.url))
        return null;

    const name = fact.source ? `${triageBriefingTexts.openSource}: ${fact.source}` : triageBriefingTexts.openSource;

    return (
        <a
            href={fact.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={name}
            title={name}
            className="inline-flex h-5 w-5 flex-none items-center justify-center self-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        >
            <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
        </a>
    );
};

const FactLinks = ({ links }) => (
    <span className="min-w-[12rem] flex-1 break-words text-[13px] text-gray-900">
        {links.map((link, index) => (
            <Fragment key={`${link.label}-${index}`}>
                {index > 0 && <span aria-hidden="true" className="text-gray-400">{triageBriefingTexts.itemSeparator}</span>}
                {isWebUrl(link.url) ? (
                    <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                    >
                        {link.label}
                    </a>
                ) : (
                    <span>{link.label}</span>
                )}
            </Fragment>
        ))}
    </span>
);

const BriefingProse = ({ fact }) => (
    <li className="py-1">
        <span className="block text-[12px] text-gray-500">{fact.label}</span>
        <p className="break-words text-[13px] leading-snug text-gray-900">{fact.value}</p>
    </li>
);

const FactEntry = ({ fact }) => (fact.links?.length ? <FactLinks links={fact.links} /> : (
    <span className="flex min-w-0 items-baseline gap-x-3">
        <FactValue value={fact.value} className="flex-1" />
        <SourceLink fact={fact} />
    </span>
));

const BriefingRow = ({ row }) => {

    if (isProse(row))
        return <BriefingProse fact={row.entries[0]} />;

    return (
        <li className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1">
            <span className="shrink-0 basis-[7.5rem] text-[12px] text-gray-500">{row.label}</span>
            <span className="flex min-w-[12rem] flex-1 flex-col gap-y-1">
                {row.entries.map((fact, index) => <FactEntry key={index} fact={fact} />)}
            </span>
        </li>
    );
};

const WhoText = ({ who }) => (
    <p className="pb-1 pl-8 pt-0.5 text-[13px] leading-snug text-gray-900">{who.text}</p>
);

const BriefingSection = ({ section, index, who }) => {

    const { Icon, tone } = stylesByUse[section.use] || neutralUseStyle;

    return (
        <section
            className={`border-b border-black/[0.06] px-3.5 py-3 ${REVEAL_CLASS}`}
            style={{ animationDelay: `${index * SECTION_STAGGER_MS}ms` }}
        >
            <div className="mb-1 flex items-center gap-2">
                <span className={`flex h-6 w-6 flex-none items-center justify-center rounded-md ${tone}`}>
                    <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                </span>
                <SectionLabel as="h3" className="text-gray-500">
                    {section.title || triageBriefingUseTitles[section.use]}
                </SectionLabel>
            </div>
            {who && <WhoText who={who} />}
            {!!section.rows.length && (
                <ul className="pl-8">
                    {section.rows.map((row, rowIndex) => (
                        <BriefingRow key={`${row.label}-${rowIndex}`} row={row} />
                    ))}
                </ul>
            )}
        </section>
    );
};

const ReadingNote = ({ stale, researchPending }) => {

    if (researchPending)
        return <p className="pb-1 pl-8 text-[11.5px] text-gray-400">{triageBriefingReadingTexts.researchNote}</p>;

    return stale
        ? <p className="pb-1 pl-8 text-[11.5px] text-amber-700">{triageBriefingReadingTexts.staleNote}</p>
        : null;
};

const AnalysisSection = ({ parts, stale, researchPending }) => (
    <section className={`border-b border-black/[0.06] bg-indigo-50/40 px-3.5 pb-4 pt-3 ${REVEAL_CLASS}`}>
        <div className="mb-1 flex items-center gap-2">
            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
                <Sparkles aria-hidden="true" className="h-3.5 w-3.5" />
            </span>
            <SectionLabel as="h3" className="text-gray-500">{triageBriefingReadingTexts.title}</SectionLabel>
        </div>
        <ReadingNote stale={stale} researchPending={researchPending} />
        {parts.map((part, partIndex) => (
            <div key={part.key} className={`mx-8 ${partIndex ? 'mt-4 border-t border-black/[0.06] pt-4' : 'pt-2'}`}>
                <h4 className="text-[14px] font-semibold leading-tight text-gray-900">{part.title}</h4>
                {part.paragraphs.map((paragraph, index) => (
                    <p
                        key={index}
                        className={`${ANALYSIS_PARAGRAPH_CLASS} ${part.leadsWithRecommendation && !index ? RECOMMENDATION_CLASS : ANALYSIS_TEXT_CLASS}`}
                    >
                        {paragraph}
                    </p>
                ))}
            </div>
        ))}
    </section>
);

const ProfileChip = ({ profile }) => {

    const profileChip = triageBriefingProfiles[profile];

    return profileChip ? <Chip tone={profileChip.tone}>{profileChip.label}</Chip> : null;
};

const BriefingToggle = ({ headline, profile, isOpen, onToggle }) => {

    const shownHeadline = headline?.trim();

    return (
        <button
            type="button"
            aria-expanded={isOpen}
            onClick={onToggle}
            className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors duration-200 hover:bg-gray-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
        >
            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[10px] bg-primaryTint text-primary">
                <FileSearch aria-hidden="true" className="h-[18px] w-[18px]" />
            </span>
            <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-[13.5px] font-semibold text-gray-900">{triageBriefingTexts.title}</span>
                    <ProfileChip profile={profile} />
                </span>
                {!isOpen && !!shownHeadline && (
                    <ClampedText as="span" lines={TRIAGE_BRIEFING_HEADLINE_LINES} className="mt-0.5 min-w-0 text-[13px] leading-snug text-gray-600">
                        {shownHeadline}
                    </ClampedText>
                )}
            </span>
            <ChevronDown
                aria-hidden="true"
                className={`h-4 w-4 flex-none text-gray-400 transition-transform duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${isOpen ? 'rotate-180' : ''}`}
            />
        </button>
    );
};

const RuleSections = ({ sections, who }) => withWhoSection(sections, who).map((section, index) => (
    <BriefingSection
        key={`${section.use}-${index}`}
        section={section}
        index={index}
        who={section.use === triageBriefingUses.who ? who : null}
    />
));

const FeedbackButton = ({ Icon, label, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-[12px] font-medium text-gray-600 transition-colors duration-200 hover:border-primary/40 hover:bg-primaryTint hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
    >
        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
        {label}
    </button>
);

const BriefingFeedback = ({ isRated, onRate }) => (
    <div className="flex flex-wrap items-center gap-2 border-t border-black/[0.06] px-3.5 py-2.5">
        {isRated ? (
            <p role="status" className="text-[12px] text-gray-500">{triageBriefingFeedbackTexts.thanks}</p>
        ) : (
            <>
                <span className="mr-auto text-[12px] text-gray-500">{triageBriefingFeedbackTexts.question}</span>
                <FeedbackButton Icon={ThumbsUp} label={triageBriefingFeedbackTexts.useful} onClick={() => onRate(true)} />
                <FeedbackButton Icon={ThumbsDown} label={triageBriefingFeedbackTexts.notUseful} onClick={() => onRate(false)} />
            </>
        )}
    </div>
);

const BriefingPanel = ({ sections, who, analysis, readingStale, researchPending, updatedAt, className, children }) => (
    <div className={`border-t border-black/[0.06] text-[13px] leading-snug text-gray-700 ${className}`}>
        {analysis && <AnalysisSection parts={analysis} stale={readingStale} researchPending={researchPending} />}
        <RuleSections sections={sections} who={who} />
        {isValidDate(updatedAt) && (
            <p className="px-3.5 pb-3 pt-2.5 text-[11.5px] text-gray-400">
                {`${triageBriefingTexts.updatedAt} ${datetimeProvider.formatDateTimeBRT(updatedAt, 'DD/MM/YYYY [às] HH:mm')}`}
            </p>
        )}
        {children}
    </div>
);

const OpenBriefing = ({ briefing, isRated, onRate, className }) => {

    const sections = filledSections(briefing);
    const who = whoOf(briefing);
    const analysis = analysisOf(briefing.reading);
    const readingStale = briefing.reading?.stale === true;
    const isAnalysisCurrent = !!analysis && !readingStale;
    const hasContent = !!analysis || !!sections.length || !!who;
    const references = referencesOf(briefing);

    return (
        <BriefingPanel
            sections={isAnalysisCurrent ? [] : sections}
            who={isAnalysisCurrent ? null : who}
            analysis={analysis}
            readingStale={readingStale}
            researchPending={briefing.reading?.researchPending === true}
            updatedAt={briefing.updatedAt}
            className={className}
        >
            {references && <BriefingReferences references={references} />}
            {hasContent && <BriefingFeedback isRated={isRated} onRate={onRate} />}
        </BriefingPanel>
    );
};

const BriefingDisclosure = ({ isOpen, children }) => (
    <Transition
        show={isOpen}
        className="grid grid-rows-[1fr]"
        enter={`transition-[grid-template-rows,opacity] duration-300 ${DISCLOSURE_EASE}`}
        enterFrom="!grid-rows-[0fr] opacity-0"
        enterTo="opacity-100"
        leave={`transition-[grid-template-rows,opacity] duration-200 ${DISCLOSURE_EASE}`}
        leaveFrom="opacity-100"
        leaveTo="!grid-rows-[0fr] opacity-0"
    >
        <div className="min-h-0 overflow-hidden">{children}</div>
    </Transition>
);

const ReferenceItem = ({ reference }) => {

    const site = siteNameOf(reference.url);
    const title = trimmedText(reference.title) || site;
    const reason = trimmedText(reference.reason);

    return (
        <li>
            <a
                href={reference.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${triageBriefingReferenceTexts.openPage}: ${title}`}
                className="group inline-flex max-w-full items-start gap-1 rounded font-medium text-gray-800 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                <span className="min-w-0 break-words">{title}</span>
                <ExternalLink aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 flex-none text-gray-400 group-hover:text-primary" />
            </a>
            <p className="break-words text-[12px] text-gray-500">{reason ? `${site}${triageBriefingTexts.itemSeparator}${reason}` : site}</p>
        </li>
    );
};

const BriefingReferences = ({ references }) => {

    const [isOpen, setIsOpen] = useState(false);
    const { used, analyzed } = references;

    return (
        <div className="border-t border-black/[0.06]">
            <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setIsOpen(!isOpen)}
                className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[12.5px] font-medium text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            >
                <FileSearch aria-hidden="true" className="h-3.5 w-3.5 flex-none text-gray-400" />
                <span className="mr-auto">{`${triageBriefingReferenceTexts.title} (${used.length})`}</span>
                <ChevronDown aria-hidden="true" className={`h-4 w-4 flex-none text-gray-400 transition-transform motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <BriefingDisclosure isOpen={isOpen}>
                <div className="space-y-2.5 px-3.5 pb-3">
                    {used.length ? (
                        <ul className="space-y-2.5">
                            {used.map(reference => <ReferenceItem key={reference.url} reference={reference} />)}
                        </ul>
                    ) : (
                        <p className="text-gray-500">{triageBriefingReferenceTexts.noneUsed}</p>
                    )}
                    <p className="text-[11.5px] text-gray-400">{triageBriefingReferenceTexts.analyzed(analyzed)}</p>
                </div>
            </BriefingDisclosure>
        </div>
    );
};

const cardFieldsOf = ({ briefing, opportunityId, surface }) => ({
    opportunityId,
    surface,
    profile: briefing.profile,
    hasAnalysis: !!analysisOf(briefing.reading),
});

const useBriefingFeedback = (card) => {

    const [ratedOpportunities, setRatedOpportunities] = useState([]);
    const isRated = ratedOpportunities.includes(card.opportunityId);

    const rate = (useful) => {

        if (isRated)
            return;

        setRatedOpportunities([...ratedOpportunities, card.opportunityId]);
        crmTriageBriefingTrackingService.trackFeedback({ ...cardFieldsOf(card), useful });
    };

    return [isRated, rate];
};

const useTrackedToggle = (card) => {

    const { opportunityId } = card;
    const [openOpportunity, setOpenOpportunity] = useState(CLOSED);
    const trackedOpportunityRef = useRef(NOT_TRACKED);
    const isOpen = openOpportunity === opportunityId;

    const trackFirstOpen = () => {

        if (trackedOpportunityRef.current === opportunityId)
            return;

        trackedOpportunityRef.current = opportunityId;
        crmTriageBriefingTrackingService.trackOpen(cardFieldsOf(card));
    };

    const open = () => {
        trackFirstOpen();
        setOpenOpportunity(opportunityId);
    };

    const toggle = () => (isOpen ? setOpenOpportunity(CLOSED) : open());

    return [isOpen, toggle];
};

const CrmTriageBriefingCard = ({ briefing, opportunityId, surface, className = '', panelClassName = '', enterDelayMs = 0 }) => {

    const card = { briefing, opportunityId, surface };
    const [isOpen, toggle] = useTrackedToggle(card);
    const [isRated, rate] = useBriefingFeedback(card);

    if (!briefing)
        return null;

    return (
        <div
            {...pageTrackingExcludeProps}
            className={`overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${REVEAL_CLASS} ${className}`}
            style={enterDelayMs ? { animationDelay: `${enterDelayMs}ms` } : undefined}
        >
            <BriefingToggle
                headline={briefing.headline}
                profile={briefing.profile}
                isOpen={isOpen}
                onToggle={toggle}
            />
            <BriefingDisclosure isOpen={isOpen}>
                <OpenBriefing briefing={briefing} isRated={isRated} onRate={rate} className={panelClassName} />
            </BriefingDisclosure>
        </div>
    );
};

export default CrmTriageBriefingCard;
